import { loadGoogleMaps } from '@rsos/maps/providers/google/loadGoogleMaps';

// Cache for elevation data to avoid repeated API calls for the same location
const elevationCache = new Map<string, number>();

/**
 * Get ground elevation for a location using Google Maps JavaScript API ElevationService.
 * Results are cached to avoid repeated API calls.
 * Will dynamically load Google Maps API if not already loaded.
 *
 * :param latitude: Location latitude
 * :param longitude: Location longitude
 * :return: Ground elevation in meters (MSL), or null if the request fails
 */
export const getGroundElevation = async (
  latitude: number,
  longitude: number,
): Promise<number | null> => {
  // Create a cache key with rounded coordinates (6 decimal places ~0.1m precision)
  const cacheKey = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

  // Check cache first
  if (elevationCache.has(cacheKey)) {
    return elevationCache.get(cacheKey) ?? null;
  }

  let maps: typeof google.maps;
  try {
    // Resolves once ElevationService is actually registered, so there is no
    // need to poll for it or re-check it afterwards.
    maps = await loadGoogleMaps('elevation');
  } catch {
    // Elevation is a nice-to-have; callers fall back to another provider.
    return null;
  }

  try {
    const elevator = new maps.ElevationService();

    return new Promise(resolve => {
      elevator.getElevationForLocations(
        { locations: [{ lat: latitude, lng: longitude }] },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const elevation = results[0].elevation;
            // Cache the result
            elevationCache.set(cacheKey, elevation);
            resolve(elevation);
          } else {
            resolve(null);
          }
        },
      );
    });
  } catch {
    return null;
  }
};
